import React, { useState, useMemo, useEffect } from "react";

import { AppointmentsAvailability } from "@secondcloset/logistics-utils";
import { Fulfillment } from "@secondcloset/types";
import { DateTimeSelector } from "@secondcloset/web-components";

import { Button, Spin, notification } from "antd";
import moment from "moment";

import { Container, Title, OrderIDWrap, Description, ButtonWrap } from "./styles";

import { useShipmentAvailabilities, useScheduleShipment } from "../../../../../../../hooks/api/fulfillment/shipment";
import Organization from "../../../../../../../lib/organization";
import { useFeatureFlags } from "hooks/api/common/useFeatureFlags";

type Shipment = Fulfillment.Shipment<"V2">;
type Order = Fulfillment.Order;
type SecondClosetShippingMethod = Fulfillment.SecondClosetShippingMethod;

interface Props {
  shipment: Shipment;
  order: Order;
  isAdmin: boolean;
  onScheduleSuccess: () => any;
  setActiveShipment?: (shipment: Shipment) => any;
}

const SchedulingView: React.FC<Props> = ({ shipment, order, isAdmin, onScheduleSuccess, setActiveShipment }) => {
  const { isEnabled: isContainerizationEnabled } = useFeatureFlags("containerization");
  const [newDate, setNewDate] = useState<string>();
  const [newTime, setNewTime] = useState<string>();
  const { shipmentAvailabilities, fetchShipmentAvailabilities } = useShipmentAvailabilities();
  const { shipmentDetails, scheduleShipment } = useScheduleShipment();
  const error = shipmentAvailabilities.error || shipmentDetails.error;

  useEffect(() => {
    const today = moment().format("YYYY-MM-DD");
    const daysAhead = moment().add(90, "days").format("YYYY-MM-DD");
    const shipmentID = shipment?.id;
    const options = {
      shipment_id: shipmentID,
      date_from: today,
      date_to: daysAhead,
      organization_id: order?.organization?.id,
      delivery_type: (shipment?.shipping_method as Fulfillment.SecondClosetShippingMethod)?.delivery_type,
    };
    fetchShipmentAvailabilities(options);

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (error) notification.error({ message: error });
  }, [error]);

  const shippingMethod = shipment?.shipping_method as SecondClosetShippingMethod;
  const hasAppointmentDate = !!shippingMethod?.date;
  const isReturn = shippingMethod?.job_type === "reverse_logistics";
  const orderNumber = order?.external_order_number;
  const isIkea = Organization.isIKEA(order?.organization);
  const deliveryType = (shipment?.shipping_method as Fulfillment.SecondClosetShippingMethod)?.delivery_type;
  const availabilityInstance = useMemo(() => {
    return new AppointmentsAvailability(shipmentAvailabilities.data || [], {
      isAdmin,
      daysTillNextAvailableBooking: deliveryType === "parcel" ? 0 : 2,
    });
    // eslint-disable-next-line
  }, [shipmentAvailabilities.data]);

  const onSelectDate = (date: string | null) => {
    setNewDate(date || undefined);
    setNewTime(undefined);
  };

  const onSelectTime = (time: string) => {
    setNewTime(time);
  };

  const isDateDisabled = (date: string) => {
    return availabilityInstance.isDateOneDisabled(date);
  };

  const isSlotDisabled = (timeslot: string) => {
    return availabilityInstance.isSlotOneDisabled(newDate || "", timeslot);
  };

  const renderDateTimeSelector = () => {
    const slots = availabilityInstance.getTimeslots(newDate || null);

    return (
      <DateTimeSelector
        selectedDate={newDate}
        selectedTime={newTime}
        onSelectDate={onSelectDate}
        onSelectTime={onSelectTime}
        disabledDate={isDateDisabled}
        disabledTime={(slot) => {
          if (isAdmin && isIkea) return false;
          return isSlotDisabled(slot || "");
        }}
        slots={slots as any}
        vertical
      />
    );
  };

  const onBookDeliveryClick = async () => {
    if (isContainerizationEnabled) {
      const hasAppointmentDate = !!(shipment?.shipping_method as SecondClosetShippingMethod)?.date;
      const isScannedToContainer = !!(shipment?.shipping_method as SecondClosetShippingMethod)?.shipment?.packages?.[0]
        ?.container?.id;
      if (hasAppointmentDate && isScannedToContainer) {
        return notification.error({
          message: "This package is scanned to a container and cannot be scheduled for delivery.",
        });
      }
    }
    const res = await scheduleShipment(shipment.id, newDate, newTime);
    if (res) {
      onScheduleSuccess();
      setActiveShipment && setActiveShipment(res);
    }
  };

  const renderSubmitButton = () => {
    return (
      <ButtonWrap>
        <Button
          disabled={!newDate || !newTime}
          type="primary"
          size="large"
          onClick={onBookDeliveryClick}
          loading={!!shipmentDetails.loading}
        >
          Book {isReturn ? "Return" : "Delivery"}
        </Button>
      </ButtonWrap>
    );
  };

  if (shipmentAvailabilities.loading) {
    return <Spin tip="Fetching availability..." />;
  }

  return (
    <Container>
      <Title>
        {hasAppointmentDate ? "Reschedule" : "Schedule"} {isReturn ? "Return" : "Delivery"} Appointment
      </Title>
      <OrderIDWrap>ORDER NUMBER: {orderNumber}</OrderIDWrap>
      <Description>Select an available date and timeslot below</Description>
      {renderDateTimeSelector()}
      {renderSubmitButton()}
    </Container>
  );
};

export default SchedulingView;
